import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { User, Star, Edit2, Save, X, Clock, AlertTriangle, Mail, Phone } from 'lucide-react';

const DescriptionCard = ({ description, onRatingUpdate, ideaId }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useContext(AuthContext);
    const [isEditing, setIsEditing] = useState(false);
    const [editContent, setEditContent] = useState(description.description);
    const [rating, setRating] = useState(description.my_rating || 0);
    const [hover, setHover] = useState(0);

    const isOwner = user && user.id === description.user.id;

    const handleRate = async (value) => {
        if (!user) {
            // Redirect to login instead of alert
            navigate('/login', { state: { from: location } });
            return;
        }

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1'}/idea_descriptions/${description.id}/ratings`,
                { rating: { score: value } }
            );

            onRatingUpdate(response.data.rating);
            setRating(value);
        } catch (err) {
            console.error(err);
            if (err.response && err.response.status === 401) {
                alert("Please log in to rate.");
            } else {
                alert("Failed to submit rating");
            }
        }
    };

    const handleEdit = async () => {
        try {
            await axios.put(
                `${process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1'}/idea_descriptions/${description.id}`,
                { idea_description: { description: editContent } }
            );
            setIsEditing(false);
            window.location.reload();
        } catch (err) {
            alert("Failed to update description");
        }
    };

    return (
        <div className="bg-surface/40 backdrop-blur-sm border border-white/5 rounded-2xl p-6 transition-all hover:bg-surface/60">
            <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                        <User className="w-5 h-5 text-secondary" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h4 className="font-bold text-white text-sm">{description.user.name}</h4>
                            {description.status === 'pending' && (
                                <span className="flex items-center gap-1 bg-yellow-500/10 text-yellow-500 text-[10px] px-2 py-0.5 rounded-full border border-yellow-500/20 font-medium">
                                    <Clock className="w-3 h-3" /> {t('details.pending')}
                                </span>
                            )}
                        </div>
                        <span className="text-xs text-slate-500 block mt-0.5">
                            {new Date(description.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                        </span>

                        {/* Contact Details */}
                        <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                            {description.user.email && (
                                <div className="flex items-center gap-1.5 text-xs text-slate-400 bg-white/5 px-2 py-1 rounded-md border border-white/5">
                                    <Mail className="w-3 h-3 text-primary" />
                                    <span>{description.user.email}</span>
                                </div>
                            )}
                            {description.user.phone_number && (
                                <div className="flex items-center gap-1.5 text-xs text-slate-400 bg-white/5 px-2 py-1 rounded-md border border-white/5">
                                    <Phone className="w-3 h-3 text-green-400" />
                                    <span>{description.user.phone_number}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {isOwner && !isEditing && (
                    <button
                        onClick={() => setIsEditing(true)}
                        className="text-slate-500 hover:text-white transition-colors p-2 hover:bg-white/5 rounded-lg"
                    >
                        <Edit2 className="w-4 h-4" />
                    </button>
                )}
            </div>

            <div className="mb-6">
                {isEditing ? (
                    <div className="space-y-4">
                        <textarea
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            className="w-full bg-black/20 border border-white/10 rounded-xl p-4 text-slate-200 focus:outline-none focus:border-primary/50 min-h-[120px] resize-y"
                        />
                        <div className="flex gap-2 justify-end">
                            <button
                                onClick={() => setIsEditing(false)}
                                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 text-slate-400 hover:text-white hover:bg-white/10 text-sm font-medium transition-colors"
                            >
                                <X className="w-4 h-4" /> {t('details.cancel')}
                            </button>
                            <button
                                onClick={handleEdit}
                                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white hover:bg-primary/90 text-sm font-medium transition-colors shadow-lg shadow-primary/20"
                            >
                                <Save className="w-4 h-4" /> {t('details.save_changes')}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="prose prose-invert prose-sm max-w-none">
                            <p className="text-slate-300 leading-relaxed font-sans text-sm">
                                {description.description}
                            </p>
                        </div>

                        {description.profit_loss_description && (
                            <div className="bg-bg-surface/50 border border-white/5 rounded-xl p-4 mt-4">
                                <h5 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-2">
                                    <AlertTriangle className="w-3 h-3" /> {t('details.profit_loss_title')}
                                </h5>
                                <p className="text-slate-400 text-sm italic">
                                    {description.profit_loss_description}
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="pt-4 border-t border-white/5 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-4 bg-white/5 px-4 py-2 rounded-full border border-white/5">
                    <div className="flex items-center gap-1.5">
                        <span className="text-lg font-bold text-white">
                            {description.average_rating || 0}
                        </span>
                        <span className="text-xs text-slate-500 font-medium uppercase tracking-wide">{t('details.avg_rating')}</span>
                    </div>
                    <div className="w-px h-8 bg-white/10 mx-2"></div>
                    <div className="text-xs text-slate-400">
                        ({description.ratings_count || 0} {t('details.votes')})
                    </div>
                </div>

                {description.status === 'pending' ? (
                    <span className="text-xs text-slate-500 bg-white/5 px-3 py-1.5 rounded-lg">
                        {t('details.rating_disabled')}
                    </span>
                ) : (
                    <div className="flex items-center gap-3">
                        <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">{t('details.rate_this')}</span>
                        <div className="flex gap-1">
                            {[...Array(5)].map((_, index) => {
                                const ratingValue = index + 1;
                                return (
                                    <button
                                        key={index}
                                        type="button"
                                        onClick={() => handleRate(ratingValue)}
                                        onMouseEnter={() => setHover(ratingValue)}
                                        onMouseLeave={() => setHover(0)}
                                        className="transition-all duration-200 hover:scale-110 focus:outline-none p-1"
                                    >
                                        <Star
                                            className={`w-5 h-5 ${ratingValue <= (hover || rating)
                                                ? 'fill-yellow-400 text-yellow-400'
                                                : 'text-slate-600 fill-transparent hover:text-slate-500'
                                                } transition-colors`}
                                        />
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DescriptionCard;
